import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { Inbox } from '../types/inbox';
import { useMemo } from 'react';
import { stableStringify } from '@Utils/searchParams';

type InboxResponse = Inbox[];
type DefaultError = unknown;

type UseInboxParams<S extends Record<string, any> = Record<string, any>> = {
  /** Optional query string params to forward to service.getMembers */
  searchParams?: S;
  /** Override default enabled behavior */
  enabled?: boolean;
};

export function useInboxes<TData = InboxResponse, TError = DefaultError>(
  { searchParams, enabled = true }: UseInboxParams = {},
  options?: Omit<
    UseQueryOptions<InboxResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const stableParams = useMemo(
    () => stableStringify(searchParams),
    [searchParams],
  );
  const queryKey: QueryKey = ['inboxes', stableParams];

  return useQuery<InboxResponse, TError, TData>({
    queryKey,
    enabled: enabled,
    queryFn: async () => {
      return service.getAllInboxes(searchParams ?? {});
    },
    ...(options as any),
  });
}
